import { classes, debounce, getTargets, parseHTML } from '~/helpers';
import { HTMLCustomElement } from '~/html-custom-element';

class SearchDrawer extends HTMLCustomElement {
  /**
   * @returns {SearchDrawerTabs}
   */
  get tabs() {
    return this.querySelector('search-drawer-tabs');
  }

  /**
   * @param {string} type
   * @returns {FormData}
   */
  getFormData(type) {
    const formData = new FormData(this.targets.form);

    formData.append('section_id', this.getAttribute('section-id'));

    if (type) {
      formData.set('type', type);
    }

    return formData;
  }

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.controller = new AbortController();

    this.on('keyup', this.targets.input, debounce(this.search.bind(this)));
    this.on('request-articles', this.tabs, this.requestArticles.bind(this));

    this.subscribe('toggle-element-opening', ({ id }) => {
      if (id === 'search-drawer') {
        if (this.targets.input.value) {
          this.search();
        }

        requestAnimationFrame(() => {
          this.targets.input.focus();
          this.targets.input.setSelectionRange(this.targets.input.value.length, this.targets.input.value.length);
        });
      }
    });
  }

  /**
   * Runs the search query and updates.
   */
  async search() {
    if (this.controller) {
      /**
       * Cancel the previous request.
       */
      this.controller.abort('Aborted pending search');
    }

    this.controller = new AbortController();

    try {
      await fetch(`${theme.routes.search_url}?${new URLSearchParams(this.getFormData('product,article')).toString()}`, {
        signal: this.controller.signal,
      })
        .then((response) => response.text())
        .then((response) => {
          const section = parseHTML(response, this.tagName.toLowerCase());

          if (section) {
            this.update(section);
          }

          this.articlesRequested = false;
        });
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Requests the article results.
   */
  async requestArticles() {
    await fetch(`${theme.routes.search_url}?&${new URLSearchParams(this.getFormData('article')).toString()}`, {
      signal: this.controller.signal,
    })
      .then((response) => response.text())
      .then((response) => {
        const section = parseHTML(response, this.tagName.toLowerCase());
        const targets = getTargets(section);

        getTargets(this).articles.firstElementChild.innerHTML = targets.articles.firstElementChild.innerHTML;

        this.articlesRequested = true;
      });
  }

  /**
   * Updates the results.
   * @param {HTMLCustomElement} section - The section element.
   */
  update(section) {
    const {
      results,
      footer,
      terms,
      'empty-footer': emptyFooter,
      'empty-categories': emptyCategories,
    } = getTargets(section) || {};

    this.targets.results.innerHTML = results.innerHTML;
    this.targets.footer.classList = footer.classList;

    if (this.targets['empty-categories']) {
      this.targets['empty-categories'].classList = emptyCategories.classList;
    }

    if (this.targets['empty-footer']) {
      this.targets['empty-footer'].classList = emptyFooter.classList;
    }

    if (this.targets.terms) {
      this.targets.terms.classList = terms.classList;
    }
  }
}

class SearchDrawerTabs extends HTMLCustomElement {
  /**
   * @returns {SearchDrawer}
   */
  get searchDrawer() {
    return this.closest('search-drawer');
  }

  connectedCallback() {
    if (this.targets['button'] && this.targets['content']) {
      this.on('click', this.targets['button'].all, (event) => {
        for (const element of [...this.targets['content'].all, ...this.targets['button'].all]) {
          element.classList.toggle(classes.active, event.currentTarget.dataset.tab === element.dataset.tab);
        }
      });

      this.on('mouseenter', this.targets.button.all, (event) => {
        if (event.currentTarget.dataset.tab === 'articles' && !this.searchDrawer.articlesRequested) {
          this.searchDrawer.requestArticles();
        }
      });
    }
  }
}

if (!customElements.get('search-drawer')) customElements.define('search-drawer', SearchDrawer);
if (!customElements.get('search-drawer-tabs')) customElements.define('search-drawer-tabs', SearchDrawerTabs);
